function toMillis(value) {
  if (!value) return NaN;
  const raw = String(value).trim().replace(' ', 'T');
  const ms = Date.parse(raw);
  return Number.isFinite(ms) ? ms : NaN;
}

function median(values) {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

export function groupCanonicalEventsByCase(dataset) {
  const grouped = new Map();
  for (const event of dataset?.events || []) {
    const caseId = String(event.caseId || '');
    if (!caseId) continue;
    if (!grouped.has(caseId)) grouped.set(caseId, []);
    grouped.get(caseId).push(event);
  }
  for (const events of grouped.values()) {
    events.sort((a, b) => String(a.timestamp || '').localeCompare(String(b.timestamp || '')) || String(a.id || '').localeCompare(String(b.id || '')));
  }
  return grouped;
}

export function canonicalCaseSequence(dataset, caseId) {
  const grouped = groupCanonicalEventsByCase(dataset);
  return (grouped.get(String(caseId)) || []).map(event => event.activity);
}

/**
 * Platform-neutral process metrics derived directly from canonical cases/events.
 * This intentionally covers only metrics that can be computed without the legacy
 * miner's synthetic Start/End nodes, calendars, filters or UI state.
 */
export function deriveCanonicalProcessMetrics(dataset) {
  const cases = dataset?.cases || [];
  const events = dataset?.events || [];
  const grouped = groupCanonicalEventsByCase(dataset);

  const activities = new Set();
  const resources = new Set();
  const variants = new Map();
  const cycleTimesSeconds = [];
  let reworkEvents = 0;
  let earliestTimestamp = '';
  let latestTimestamp = '';

  for (const event of events) {
    if (event.activity) activities.add(String(event.activity));
    if (event.resource) resources.add(String(event.resource));
    const ts = String(event.timestamp || '');
    if (ts && (!earliestTimestamp || ts < earliestTimestamp)) earliestTimestamp = ts;
    if (ts && (!latestTimestamp || ts > latestTimestamp)) latestTimestamp = ts;
  }

  for (const c of cases) {
    const caseEvents = grouped.get(String(c.id)) || [];
    if (caseEvents.length) {
      const sequence = caseEvents.map(e => String(e.activity || '')).join(' -> ');
      variants.set(sequence, (variants.get(sequence) || 0) + 1);
      const seen = new Set();
      for (const event of caseEvents) {
        const activity = String(event.activity || '');
        if (seen.has(activity)) reworkEvents += 1;
        else seen.add(activity);
      }
    }

    const start = toMillis(c.createdAt || caseEvents[0]?.timestamp);
    const finish = toMillis(c.closedAt || caseEvents[caseEvents.length - 1]?.timestamp);
    if (Number.isFinite(start) && Number.isFinite(finish) && finish >= start) {
      cycleTimesSeconds.push((finish - start) / 1000);
    }
  }

  const cycleTimeTotal = cycleTimesSeconds.reduce((sum, value) => sum + value, 0);
  return {
    caseCount: cases.length,
    eventCount: events.length,
    activityCount: activities.size,
    resourceCount: resources.size,
    variantCount: variants.size,
    reworkEventCount: reworkEvents,
    averageCycleTimeSeconds: cycleTimesSeconds.length ? cycleTimeTotal / cycleTimesSeconds.length : 0,
    medianCycleTimeSeconds: median(cycleTimesSeconds),
    earliestTimestamp,
    latestTimestamp,
    variants: Array.from(variants.entries())
      .map(([sequence, caseCount]) => ({ sequence, caseCount }))
      .sort((a, b) => b.caseCount - a.caseCount || a.sequence.localeCompare(b.sequence)),
  };
}
